/**
 * AnnounceModal — admin modal to email all learners about a module change.
 *
 * Two open modes:
 *   • kind="new_module" — opened automatically after a successful publish
 *   • kind="update"     — opened from the standalone "Notify learners"
 *                          button on the module editor
 *
 * Flow:
 *   1. Mount → POST /api/announcements/preview to get auto-filled
 *      subject/body + learner count.
 *   2. Admin can edit subject + body freely (max 200 chars / 4000 chars).
 *   3. "Send to N learners" → POST /api/announcements/send → toast +
 *      results breakdown (sent / failed). Modal closes on success.
 */
import React, { useEffect, useState } from "react";
import { Mail, AlertCircle, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function AnnounceModal({ open, onClose, moduleId, kind = "new_module" }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [learnerCount, setLearnerCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null); // {sent, failed}

  useEffect(() => {
    if (!open || !moduleId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setSentResult(null);
        const { data } = await api.post("/announcements/preview", { module_id: moduleId, kind });
        if (cancelled) return;
        setSubject(data.subject || "");
        setBody(data.body || "");
        setLearnerCount(data.learner_count || 0);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load announcement preview.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, moduleId, kind]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (sending) return;
    if (subject.trim().length < 3 || body.trim().length < 10) {
      setError("Subject must be ≥ 3 chars and body ≥ 10 chars.");
      return;
    }
    try {
      setSending(true);
      setError(null);
      const { data } = await api.post("/announcements/send", {
        module_id: moduleId,
        kind,
        subject: subject.trim(),
        body: body.trim(),
      });
      setSentResult({ sent: data.sent, failed: data.failed });
      toast.success(
        `Announcement sent to ${data.sent} learner${data.sent === 1 ? "" : "s"}`,
        { description: data.failed ? `${data.failed} delivery failure${data.failed === 1 ? "" : "s"} — see audit log` : undefined },
      );
      // Brief pause so admins can read the result, then close.
      setTimeout(() => onClose?.(), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed. Please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[950] bg-[#222222]/85 backdrop-blur-sm flex items-stretch justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="announce-title"
      data-testid="announce-modal"
    >
      <div className="w-full max-w-2xl m-4 self-center bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-[#222222] to-[#1a1a1a] text-white flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Mail className="text-[#E53935]" size={22} />
              <h2 id="announce-title" className="text-lg font-semibold tracking-tight">
                {kind === "new_module" ? "Announce new module" : "Announce module update"}
              </h2>
            </div>
            <p className="text-sm text-slate-300 mt-1">
              Compose the email learners will receive. Subject and body are auto-filled — edit freely.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={sending}
            className="text-slate-300 hover:text-white disabled:opacity-30"
            aria-label="Close"
            data-testid="announce-close"
          >
            <X size={20}/>
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center" data-testid="announce-loading">
            <Loader2 className="animate-spin text-[#E53935]" size={28}/>
          </div>
        ) : sentResult ? (
          <div className="p-8 text-center" data-testid="announce-result">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={42}/>
            <div className="text-lg font-semibold text-[#222222]">
              Sent to {sentResult.sent} learner{sentResult.sent === 1 ? "" : "s"}
            </div>
            {sentResult.failed > 0 && (
              <div className="text-sm text-amber-700 mt-1">
                {sentResult.failed} delivery failure{sentResult.failed === 1 ? "" : "s"} — check the audit log.
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="announce-recipient-count">
              <AlertCircle size={14} className="text-[#E53935]"/>
              <span>This will email <strong>{learnerCount}</strong> learner{learnerCount === 1 ? "" : "s"}.</span>
            </div>
            <div>
              <Label htmlFor="ann-subject" className="text-sm">Subject</Label>
              <Input
                id="ann-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                data-testid="announce-subject"
                className="mt-1"
              />
              <div className="text-[11px] text-slate-400 mt-1">{subject.length}/200</div>
            </div>
            <div>
              <Label htmlFor="ann-body" className="text-sm">Body</Label>
              <textarea
                id="ann-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={4000}
                rows={9}
                data-testid="announce-body"
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/30 focus:border-[#E53935]"
              />
              <div className="text-[11px] text-slate-400 mt-1">{body.length}/4000 — plain text. We&rsquo;ll wrap it in branded HTML automatically.</div>
            </div>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="announce-error">{error}</div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={sending}
                data-testid="announce-cancel"
              >
                Skip / cancel
              </Button>
              <Button
                onClick={send}
                disabled={sending || learnerCount === 0}
                data-testid="announce-send"
                className="bg-[#E53935] hover:bg-[#c62828] text-white disabled:opacity-50"
              >
                {sending ? `Sending to ${learnerCount}…` : `Send to ${learnerCount} learner${learnerCount === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnnounceModal;
